import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import { KeyRound, Loader2 } from "lucide-react";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [isRecovery, setIsRecovery] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        // 1) Erreur explicite renvoyée par Supabase (lien expiré, etc.)
        const hash = window.location.hash || "";
        const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
        const errorDescription = hashParams.get("error_description") || searchParams.get("error_description");
        const errorCode = hashParams.get("error_code") || searchParams.get("error_code");
        if (errorDescription || errorCode) {
          setErrorMsg(decodeURIComponent(errorDescription || errorCode || "Lien invalide"));
          setChecking(false);
          return;
        }

        // 2) Flow PKCE (nouveau format) : ?code=...
        const code = searchParams.get("code");
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            setErrorMsg("Ce lien de réinitialisation est invalide ou a expiré.");
            setChecking(false);
            return;
          }
          setIsRecovery(true);
          // Nettoyer l'URL
          window.history.replaceState({}, "", "/reset-password");
          setChecking(false);
          return;
        }

        // 3) Flow Hash (ancien format) : #access_token=...&type=recovery
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        const type = hashParams.get("type");
        if (accessToken && type === "recovery") {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken ?? "",
          });
          if (error) {
            setErrorMsg("Ce lien de réinitialisation est invalide ou a expiré.");
            setChecking(false);
            return;
          }
          setIsRecovery(true);
          window.history.replaceState({}, "", "/reset-password");
          setChecking(false);
          return;
        }

        // 4) Cas où Supabase a déjà émis un événement PASSWORD_RECOVERY
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          // L'utilisateur a une session active — on autorise la mise à jour du mot de passe
          // (peut arriver si le hash a déjà été consommé par onAuthStateChange)
          setIsRecovery(true);
          setChecking(false);
          return;
        }

        // Aucun token trouvé
        setErrorMsg("Lien de réinitialisation invalide. Veuillez en demander un nouveau.");
        setChecking(false);
      } catch (e) {
        console.error("Reset password init error:", e);
        setErrorMsg("Une erreur est survenue. Veuillez réessayer.");
        setChecking(false);
      }
    };

    // Listener pour PASSWORD_RECOVERY (au cas où il arrive après init)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsRecovery(true);
        setErrorMsg(null);
        setChecking(false);
      }
    });

    init();

    return () => subscription.unsubscribe();
  }, [searchParams]);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({ title: "Erreur", description: "Le mot de passe doit contenir au moins 6 caractères.", variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: "Erreur", description: "Les mots de passe ne correspondent pas.", variant: "destructive" });
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Mot de passe mis à jour", description: "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe." });
      await supabase.auth.signOut();
      navigate("/auth");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex-1 w-full flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md shadow-lg border-none">
            <CardContent className="flex flex-col items-center gap-3 py-10">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Vérification du lien…</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!isRecovery) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex-1 w-full flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md shadow-lg border-none">
            <CardHeader className="text-center">
              <CardTitle>Lien invalide</CardTitle>
              <CardDescription>{errorMsg ?? "Ce lien de réinitialisation est invalide ou a expiré."}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/auth")} className="w-full rounded-full">
                Retour à la connexion
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="flex-1 w-full flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-lg border-none">
          <CardHeader className="text-center">
            <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mx-auto mb-3">
              <KeyRound className="w-7 h-7 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl">Nouveau mot de passe</CardTitle>
            <CardDescription>Choisissez un nouveau mot de passe pour votre compte</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Nouveau mot de passe</Label>
                <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input id="confirmPassword" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              </div>
              <Button type="submit" className="w-full rounded-full" size="lg" disabled={loading}>
                {loading ? "Mise à jour..." : "Mettre à jour le mot de passe"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
